import { useId, useState } from 'react'
import cn from 'classnames'
import type { Course } from '@mc/api'
import { MasterClassWordmark, usePrimaryCta } from '@mc/growth'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { getNumberOfInstructors } from '@mc/instructors'
import { usePromotion } from '@mc/promo-utils'
import { useStaticData } from '@mc/static-data-react'
import { Locations } from '@mc/track-event'
import { TrackingContainer } from '@mc/visibility'
import CTA from 'components/CTA'
import { DiagonalBanner } from 'pages/classes/[slug]/components/Hero/DiagonalBanner'
import heroClasses from 'pages/classes/[slug]/components/Hero/Hero.module.scss'
import { type Plan } from '../constants'
import { trackPlanCardClicked, trackProHeroViewed } from '../events'
import { useCtaOverrideTrackProps } from '../hooks/useCtaOverrideTrackProps'
import { EXPERIMENT_I18N_NAMESPACE, i18nInstance } from '../i18n'
import { withProPlan } from '../preselectedPlan'
import { PlanCard } from './PlanCard'
import { PriceLine } from './PriceLine'

const trackProps = {
  category: 'video-playlist-module',
  content_type: 'product',
  location: Locations.HERO,
}

const ProCtaCopy = ({ copy }: { copy: string }) => {
  const [before, after] = copy.split('MasterClass')

  return (
    <span>
      <span className='mc-sr-only'>{copy}</span>
      <span aria-hidden='true' className='d-inline-flex align-items-baseline'>
        {before}
        <MasterClassWordmark height={12} className='mc-mx-1' />
        {after}
      </span>
    </span>
  )
}

const DualCtaSectionComp = () => {
  const { t } = useTranslation(EXPERIMENT_I18N_NAMESPACE)
  const { course } = useStaticData<{ course: Course }>()
  const slug = course?.slug ?? ''
  const { ctaHref } = usePrimaryCta()
  const ctaOverrideTrackProps = useCtaOverrideTrackProps()
  const { isPromotionOn } = usePromotion()
  const [selected, setSelected] = useState<Plan>('masterclass')
  const headlineId = useId()
  // The hero mounts a desktop and a mobile copy; a shared radio name would merge
  // both copies into one native group and desync their checked state.
  const groupName = useId()
  const isPro = selected === 'pro'
  const ctaCopy = t(`cta.${selected}`)

  const handleSelect = (plan: Plan) => {
    if (plan === selected) return
    trackPlanCardClicked(plan, slug)
    setSelected(plan)
  }

  return (
    <TrackingContainer
      onFirstVisible={() => trackProHeroViewed(slug)}
      className={cn(
        'mc-px-6 mc-py-4 mc-mt-8 flex-shrink-0 mc-text--center position-relative mc-overflow--hidden',
        heroClasses.pricingContainer,
      )}
    >
      {isPromotionOn && <DiagonalBanner />}
      <p id={headlineId} className='mc-text-h5 mc-mb-5'>
        {t('headline')}
      </p>
      <div
        role='radiogroup'
        aria-labelledby={headlineId}
        className='d-flex flex-column mc-gap-2'
      >
        <PlanCard
          plan='masterclass'
          groupName={groupName}
          name={t('masterclass.name')}
          description={t('masterclass.description', {
            numberOfClasses: getNumberOfInstructors(),
          })}
          perks={t('masterclass.perks', { returnObjects: true }) as string[]}
          checked={selected === 'masterclass'}
          onSelect={handleSelect}
        />
        <PlanCard
          plan='pro'
          groupName={groupName}
          name={t('pro.name')}
          description={t('pro.description')}
          perks={t('pro.perks', { returnObjects: true }) as string[]}
          checked={selected === 'pro'}
          onSelect={handleSelect}
        />
      </div>
      <CTA
        fullWidth
        href={isPro ? withProPlan(ctaHref) : undefined}
        trackProps={{
          ...trackProps,
          ...(isPro && ctaOverrideTrackProps),
          action: ctaCopy,
        }}
        className='mc-mt-5 mc-mb-2'
      >
        <ProCtaCopy copy={ctaCopy} />
      </CTA>
      <PriceLine />
    </TrackingContainer>
  )
}

export const DualCtaSection = withI18nContext(i18nInstance)(DualCtaSectionComp)
